import useAppDispatch from "../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useGetAllLastMinuteDealQuery } from "../../api/queries";
import { Table, TableProps } from "antd";
import { handleError } from "../../helper/errorHandler";
import { ColumnsType } from "antd/es/table";
import dots from "../../assets/images/icons/dots.svg";
import editIcon from "../../assets/images/icons/edit-dark.svg";
import deleteIcon from "../../assets/images/icons/basket-black.svg";
import MainTitle from "../../components/ui/MainTitle/MainTitle";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import plusIconWhite from "../../assets/images/icons/plus-white.svg";
import { ILastMinuteDeal } from "../../models/LastMinuteDeal";
import CreateLastMinuteDeal from "./CreateLastMinuteDeal/CreateLastMinuteDeal";
import ConfirmPopup from "../../components/ConfirmPopup/ConfirmPopup";
import { useLastMinuteDealMutations } from "../../api/mutations";
import { openSuccessMessage } from "../../store/message/messageSlice";

const LastMinuteDeals = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [selectedIdForDelete, setSelectedIdForDelete] = useState<string | null>(null);
  const [selectedIdForCreatePopup, setSelectedIdForCreatePopup] = useState<string | "new" | null>(
    null
  );
  const [tableParams, setTableParams] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const {
    data = { items: [], total: 0 },
    isLoading,
    refetch,
  } = useGetAllLastMinuteDealQuery({
    currentPage: tableParams.current,
    pageSize: tableParams.pageSize,
  });
  const { remove } = useLastMinuteDealMutations();

  const handleTableChange: TableProps<ILastMinuteDeal>["onChange"] = (pagination) => {
    setTableParams({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const deleteDealFn = () => {
    remove(selectedIdForDelete!, {
      onSuccess: () => {
        dispatch(openSuccessMessage(t("LastMinuteDealDeletedSuccessfully")));
        refetch();
        setSelectedIdForDelete(null);
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const columns: ColumnsType<ILastMinuteDeal> = [
    {
      title: t("TimeBeforeClass"),
      render: (data: ILastMinuteDeal) => {
        return `${data.hoursBeforeClass} hours`;
      },
    },
    {
      title: `${t("PercentAmount")} (%)`,
      render: (data: ILastMinuteDeal) => {
        return `${data.discountPercent}`;
      },
    },
    {
      title: t("Action"),
      width: "150px",
      render: (data: ILastMinuteDeal) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li className={"dropdown-item"} onClick={() => setSelectedIdForCreatePopup(data.id)}>
                <img src={editIcon} alt={"Edit"} />
                {t("Edit")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedIdForDelete(data.id)}>
                <img src={deleteIcon} alt={"Delete"} />
                {t("Delete")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  return (
    <div className={"page-content gyms-page"}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <MainTitle text={t("LastMinuteDeal")} />
        <CustomButton
          text={t("AddNew")}
          type={"button"}
          typeStyle={"fill"}
          icon={plusIconWhite}
          altIcon={"add"}
          functionOnClick={() => setSelectedIdForCreatePopup("new")}
          style={{ marginBottom: "16px", width: "180px" }}
        />
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data.items}
        pagination={{ ...tableParams, total: data.total }}
        loading={isLoading}
        onChange={handleTableChange}
      />

      {selectedIdForCreatePopup && (
        <CreateLastMinuteDeal
          id={selectedIdForCreatePopup}
          refetch={refetch}
          closePopup={() => setSelectedIdForCreatePopup(null)}
        />
      )}
      {selectedIdForDelete && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToDeleteThisRule")}
          onClickYes={deleteDealFn}
          onClickNo={() => setSelectedIdForDelete(null)}
        />
      )}
    </div>
  );
};

export default LastMinuteDeals;
